import { z } from 'zod';
import { DifficultySchema } from '../enums/difficulty.js';
import { CategorySchema } from '../enums/category.js';
import { CognitiveLevelSchema } from './cognitive-level.js';
import { ChunkRefSchema } from './source-ref.js';
import { ItemTagsSchema, coerceItemTagsV3ToV4 } from './item-tags.js';
import { ConceptOmissionSchema } from './concept-omission.js';
// Session 19AE: full .describe() for LLM clarity.
//
// id patterns: `mcq1_07` (case-numbered), `mcqb1_01` (Brand topic-prefixed),
// `mcq_cross_01` (cross-case). caseId nullable for cross-case items (v3);
// v4 (19BH Day 2 Step 2.4): caseIds is the canonical multi-chapter form,
// caseId stays as deprecated optional alias for engine back-compat.
export const MCQOptionSchema = z
    .string()
    .regex(/^[ABCD]\) /, 'option must start with "A) " | "B) " | "C) " | "D) "');
export const McqSchema = z.preprocess(coerceItemTagsV3ToV4, ItemTagsSchema.extend({
    id: z.string().regex(/^mcq[a-z0-9_]+$/, 'mcq id must start with "mcq" (lowercase, snake_case)')
        .describe('Unique snake_case slug starting with `mcq` (e.g. `mcq_logic_07`).'),
    /**
     * @deprecated v3 alias of `caseIds[0]`. Day 5 finalize emits this for
     * runtime engine back-compat; v4 LLM should populate `caseIds[]` directly.
     * 19BI removes.
     */
    caseId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of caseIds[0]. Set by Day 5 finalize for engine back-compat; new code should read caseIds[].'),
    /** @deprecated v3 alias of `topicIds[0]`. */
    topicId: z.string().nullable().optional()
        .describe('@deprecated v3 alias of topicIds[0]. Set by Day 5 finalize; new code should read topicIds[].'),
    subtopicId: z.string().nullable().optional()
        .describe('Subtopic id with topic prefix. null/omit if not applicable.'),
    section: z.string().nullable()
        .describe('Sub-section label within the case for split cases (else null).'),
    difficulty: DifficultySchema
        .describe('easy / medium / hard. Distribution target: ~30% easy, ~50% medium, ~20% hard.'),
    cognitiveLevel: CognitiveLevelSchema.optional()
        .describe('Bloom 5-tier level. Distractor design should match (recall items ≠ analyze items).'),
    mockOnly: z.boolean()
        .describe('If true, this MCQ appears only in mock-exam papers, NOT the daily quiz pool.'),
    category: CategorySchema
        .describe('Question category: framework / fact / causal / strategy / comparison / concept / policy. Drives quiz filtering.'),
    question: z.string().min(20)
        .describe('Question stem. Concrete + answerable from a single piece of knowledge. ≥20 chars (a meaningful stem · not "What?"). Avoid "Which of the following..." with 4 unrelated options. Markdown allowed.'),
    options: z.array(MCQOptionSchema).length(4)
        .describe('Exactly 4 options labeled `A) ` `B) ` `C) ` `D) `. ALL FOUR must be plausible — distractors come from common confusions / wrong frameworks / off-by-one errors / similar-sounding terms. Length parity (within 1.5x of each other). NO "all of the above" / "none of the above".'),
    correct: z.number().int().min(0).max(3)
        .describe('Index (0-3) of the correct option. 0 → A, 1 → B, 2 → C, 3 → D.'),
    explanation: z.string().min(80)
        .describe('Why correct is right AND why each distractor is wrong. ≥80 chars (a real explanation · not "Because A is right"). Markdown structure: lead with **A/B/C/D 是正确答案 because...**, then bullet list of distractors with their failure mode. Inline math allowed. Constraint added Session 19AO to fight LLM laziness — see CONTENT_SPEC.'),
    /** @deprecated v3 alias of `conceptIds[0]`. */
    conceptTag: z.string().min(1).max(80).nullish()
        .describe('@deprecated v3 alias of conceptIds[0]. Set by Day 5 finalize; new code should read conceptIds[].'),
    sourceRef: ChunkRefSchema.nullish()
        .describe('19BG+2 Phase 5 (DEBT-AW-SOURCEREF): citation anchor pointing back to a specific source chunk. Snippet field is substring-verified against source markdown by the source-ref lint rule. Drives fictional metric. Optional for back-compat.'),
    /** 19BH Day 2 Step 2.4 · K18 soft escape — LLM self-reports concepts it
     *  couldn't cover instead of hard-failing the bundle. Audit (Day 3) turns
     *  these into GapReport entries with severity='warn'. */
    omittedConcepts: z.array(ConceptOmissionSchema).nullish()
        .describe('K18 soft escape · concepts the LLM declared materially insufficient for this stage. Audit converts each entry into a GapEntry (severity warn).'),
}).passthrough());
export const McqsSchema = z.array(McqSchema);
//# sourceMappingURL=mcq.js.map